import { useState, useEffect } from "react";
import {
  Modal,
  Table,
  InputNumber,
  Input,
  Tag,
  Button,
  Space,
  Typography,
  Alert,
  message,
  Upload,
  DatePicker,
} from "antd";
import {
  EnvironmentOutlined,
  BankOutlined,
  FilePdfOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import Dragger from "antd/es/upload/Dragger";
import axios from "axios";
import { HEADERS_UPIMAGE, POST_DELETE_IMAGE_NEW } from "../../../API/apiUrls";
import Compressor from "compressorjs";

const { Text } = Typography;

const baht = {
  formatter: (v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ","),
  parser: (v) => `${v}`.replace(/\$\s?|(,*)/g, ""),
};

const typeLandLabel = (v) => (v === 1 ? "โฉนด" : v === 2 ? "นส.3" : (v ?? "-"));

const ownerType = (garno) =>
  garno === 0 ? "ผู้เช่าซื้อ" : garno ? `คนค้ำที่ ${garno}` : "-";

/* แปลงที่ยึดไม่ได้: ติดอายัด หรือ ติดขายฝาก */
const blockReason = (l) =>
  l.sequestrateStatus === 1
    ? "ติดอายัด"
    : l.mortgageType === "ขายฝาก"
      ? "ติดขายฝาก"
      : null;

/* ================================================================== */
/* Modal เลือกแปลงยึด — รวมทุกแปลงของสัญญาเป็นลิสต์เดียว (ไม่แยกคน)        */
/* บันทึกออกมาเป็น array [] ตามลำดับแปลง                                 */
/* ================================================================== */
const SeizeAssetModal = ({ open, close, contract, onSaved }) => {
  const lands = contract?.lands || [];

  // แยกการแสดงผล: ผู้เช่าซื้อ (garno = 0) / ผู้ค้ำ (garno >= 1)
  const buyerLands = lands.filter((l) => l.garno === 0);
  const guarantorLands = lands.filter((l) => l.garno >= 1);
  // ลำดับรวม (ตามลำดับใน lands) เพื่อโชว์เลขให้ต่อเนื่องข้ามกลุ่ม
  const orderIndex = (landId) =>
    lands.findIndex((l) => l.landId === landId) + 1;

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  // landId -> { estimatedEnforcePrice, legalExecutionOffice }
  const [rowData, setRowData] = useState({});
  const [fileList, setFileList] = useState([]);
  const [allImg, setAllImg] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [loading, setLoading] = useState(false);

  // ตั้งค่าเริ่มต้นเมื่อเปิด modal (prefill ของเดิมถ้าเคยบันทึก)
  useEffect(() => {
    if (!open) return;
    const init = {};
    lands.forEach((l) => {
      if (l.estimatedEnforcePrice != null || l.legalExecutionOffice) {
        init[l.landId] = {
          estimatedEnforcePrice: l.estimatedEnforcePrice ?? null,
          legalExecutionOffice: l.legalExecutionOffice ?? null,
        };
      }
    });
    setRowData(init);
    setSelectedRowKeys(
      lands.filter((l) => l.seizeStatus === 1).map((l) => l.landId),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contract]);

  const setField = (landId, field, value) =>
    setRowData((prev) => ({
      ...prev,
      [landId]: { ...prev[landId], [field]: value },
    }));

  const handleSave = () => {
    if (selectedRowKeys.length === 0) {
      message.warning("กรุณาเลือกแปลงที่ต้องการยึดอย่างน้อย 1 แปลง");
      return;
    }
    // ต้องกรอก จพค. + สถานที่บังคับคดี ครบทุกแปลงที่เลือก
    const missing = selectedRowKeys.filter((k) => {
      const d = rowData[k] || {};
      return (
        d.estimatedEnforcePrice == null ||
        d.estimatedEnforcePrice === "" ||
        !d.legalExecutionOffice ||
        !String(d.legalExecutionOffice).trim()
      );
    });
    if (missing.length > 0) {
      message.error(
        "กรุณากรอกราคาประเมิน (จพค.) และสถานที่บังคับคดี ให้ครบทุกแปลงที่เลือก",
      );
      return;
    }

    // เก็บเป็น array ตามลำดับของ lands (ไม่แยกผู้เช่าซื้อ/คนค้ำ)
    const seizeList = lands
      .filter((l) => selectedRowKeys.includes(l.landId))
      .map((l) => ({
        ...l,
        estimatedEnforcePrice: rowData[l.landId].estimatedEnforcePrice,
        legalExecutionOffice: rowData[l.landId].legalExecutionOffice,
        seizeStatus: 1, // 1 = ตั้งเรื่องยึด (รอใส่รายงานการยึด)
      }));

    onSaved?.(seizeList);
    message.success(
      `บันทึกแปลงยึด ${seizeList.length} แปลง — รอใส่รายงานการยึด`,
    );
    close(false);
  };

  const columns = [
    {
      title: "ลำดับ",
      key: "i",
      align: "center",
      width: 60,
      render: (_, r) => orderIndex(r.landId),
    },
    {
      title: "เลขโฉนด/ที่ดิน",
      dataIndex: "numberLand",
      key: "numberLand",
      align: "center",
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v}</Text>
          <Tag color="blue" style={{ margin: 0 }}>
            {typeLandLabel(r.typeLand)}
          </Tag>
        </Space>
      ),
    },
    {
      title: "เจ้าของ / ที่ตั้ง",
      key: "owner",
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          <Text>{r.ownerName}</Text>
          <Tag>{ownerType(r.garno)}</Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <EnvironmentOutlined /> {r.subDistDesc} {r.distDesc} {r.provDesc}
          </Text>
        </Space>
      ),
    },
    {
      title: "ภาระ/สถานะ",
      key: "burden",
      align: "center",
      render: (_, r) => {
        const reason = blockReason(r);
        if (reason) return <Tag color="red">{reason} (ยึดไม่ได้)</Tag>;
        if (r.mortgageType === "จำนอง")
          return <Tag color="orange">ติดจำนอง</Tag>;
        return <Tag color="green">ยึดได้</Tag>;
      },
    },
    {
      title: "ราคาประเมิน (จพค.)",
      key: "enforcePrice",
      align: "center",
      width: 190,
      render: (_, r) => {
        const blocked = !!blockReason(r);
        return (
          <InputNumber
            {...baht}
            suffix="บาท"
            placeholder="0"
            disabled={blocked}
            value={rowData[r.landId]?.estimatedEnforcePrice}
            onChange={(v) => setField(r.landId, "estimatedEnforcePrice", v)}
            style={{ width: "100%" }}
            size="large"
          />
        );
      },
    },
    {
      title: "สถานที่บังคับคดี",
      key: "office",
      width: 200,
      render: (_, r) => {
        const blocked = !!blockReason(r);
        return (
          <Input
            prefix={<BankOutlined style={{ color: "#bbb" }} />}
            placeholder="เช่น สนง.บังคับคดี จ.หนองคาย"
            disabled={blocked}
            value={rowData[r.landId]?.legalExecutionOffice}
            onChange={(e) =>
              setField(r.landId, "legalExecutionOffice", e.target.value)
            }
          />
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (r) => ({ disabled: !!blockReason(r) }),
  };

  // หัวข้อกลุ่ม + จำนวนที่เลือกในกลุ่มนั้น
  const SectionTitle = ({ color, label, items }) => {
    const picked = items.filter((l) =>
      selectedRowKeys.includes(l.landId),
    ).length;
    return (
      <Space style={{ margin: "4px 0 8px" }}>
        <Tag color={color} style={{ fontSize: 13 }}>
          {label}
        </Tag>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {items.length} แปลง · เลือก {picked}
        </Text>
      </Space>
    );
  };

  const sectionTable = (items) => (
    <Table
      size="small"
      rowKey={(r) => r.landId}
      columns={columns}
      dataSource={items}
      rowSelection={rowSelection}
      pagination={false}
      scroll={{ x: 900 }}
    />
  );

  const compressImage = async (file, types, index) => {
    return new Promise((resolve) => {
      let quality;
      if (file.size >= 2500000) quality = 0.1;
      else if (file.size >= 2000000) quality = 0.15;
      else if (file.size >= 1500000) quality = 0.2;
      else if (file.size >= 1000000) quality = 0.3;
      else quality = 0.5;

      new Compressor(file, {
        quality: quality,
        success: (result) => {
          const fileOptions = {
            type: result.type,
            lastModified: Date.now(),
          };
          const compressedFileAsFile = new File(
            [result],
            file.name,
            fileOptions,
          );

          const image = {
            imageCompressedFile: compressedFileAsFile,
            type: types,
            originalIndex: index,
          };

          setAllImg((prev) => [...prev, image]);
          setLoading(false);
          resolve(true);
        },
        error: (err) => {
          console.error("Compressor error:", err);
          message.error(`ไม่สามารถบีบอัดไฟล์ "${file.name}" ได้`);
          setLoading(false);
          resolve(true);
        },
      });
    });
  };

  // ลบรูป (รับ array)
  const handleDeleteIMG = async (images) => {
    if (!images || images.length === 0) return;

    setLoading(true);
    try {
      const res = await axios.delete(POST_DELETE_IMAGE_NEW, {
        data: images,
        headers: HEADERS_UPIMAGE,
      });

      if (res.status === 200) {
        const selectedPaths = new Set(images.map((img) => img.path));
        message.success("ลบรูปสำเร็จ");
      }
    } catch (err) {
      message.error("เกิดข้อผิดพลาด ลบรูปไม่สำเร็จ");
      console.error("err", err);
    } finally {
      setLoading(false);
    }
  };

  // ลบรูปเดียว - ส่งเป็น array
  const handleDeleteSingleImage = async (image) => {
    await handleDeleteIMG([image]);
  };

  // Preview รูป
  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handlePreview = async (file) => {
    if (file.type === "application/pdf" || file.name.endsWith(".pdf")) {
      let pdfUrl = file.url;
      if (!pdfUrl && file.originFileObj) {
        pdfUrl = URL.createObjectURL(file.originFileObj);
      }
      window.open(pdfUrl, "_blank");
      return;
    }

    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  // Handle change file list
  const handleChangeIMG = ({ fileList: newFileList, file }) => {
    const updatedFileList = newFileList.map((f) => f);

    setFileList(updatedFileList);

    if (file.status === "removed") {
      setAllImg((prevAllImg) => {
        return prevAllImg.filter((img) => img.originalIndex !== file.uid);
      });
    }

    if (
      file.status === "done" ||
      file.status === "removed" ||
      file.status === "error"
    ) {
      setLoading(false);
    }
  };

  // Before upload
  const beforeUpts = async (file, ty) => {
    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");

    if (isPdf) {
      const image = {
        imageCompressedFile: file,
        type: ty,
        originalIndex: file.uid,
      };
      setAllImg((prev) => [...prev, image]);
      return false;
    }

    if (file.size > 5000000) {
      message.warning(`ไฟล์ "${file.name}" เกิน 5MB`);
      return Upload.LIST_IGNORE;
    }

    try {
      setLoading(true);
      await compressImage(file, ty, file.uid);
      return false;
    } catch (error) {
      console.error("File processing error:", error);
      message.error("File processing failed.");
      return Upload.LIST_IGNORE;
    }
  };

  // Upload Props
  const uploadProps = {
    name: "file",
    multiple: true,
    accept: "image/*,.pdf",
    listType: "picture",
    fileList: fileList,
    onPreview: handlePreview,
    onChange: handleChangeIMG,
    beforeUpload: (file) => beforeUpts(file, 3),
    iconRender: (file) => {
      if (file.type === "application/pdf" || file.name.endsWith(".pdf")) {
        return (
          <FilePdfOutlined style={{ fontSize: "48px", color: "#ff4d4f" }} />
        );
      }
      return null;
    },
  };

  return (
    <Modal
      title={
        contract ? (
          <Space direction="vertical" size={0}>
            <Text strong>เลือกแปลงที่ต้องการยึด</Text>
            <Text type="secondary" style={{ fontSize: 13 }}>
              สัญญา {contract.contno} · {contract.ownerName} · {lands.length}{" "}
              แปลง
            </Text>
          </Space>
        ) : (
          "เลือกแปลงที่ต้องการยึด"
        )
      }
      open={open}
      onCancel={() => close(false)}
      width={1200}
      maskClosable={false}
      footer={
        <div style={{ textAlign: "right" }}>
          <Text type="secondary" style={{ marginRight: 12 }}>
            เลือกแล้ว {selectedRowKeys.length} แปลง
          </Text>
          <Button
            onClick={() => close(false)}
            style={{ color: "#cf1322", marginRight: 12 }}
          >
            ปิด
          </Button>
          <Button type="primary" onClick={handleSave}>
            บันทึกแปลงยึด
          </Button>
        </div>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="เลือกแปลงที่จะยึด แล้วกรอกราคาประเมิน (จพค.) และสถานที่บังคับคดี ให้ครบทุกแปลงที่เลือก"
        description="แปลงที่ติดอายัด หรือ ติดขายฝาก จะยึดไม่ได้ (ติ๊กไม่ได้)"
      />

      {/* กลุ่มผู้เช่าซื้อ */}
      <SectionTitle
        color="blue"
        label="ทรัพย์ของผู้เช่าซื้อ"
        items={buyerLands}
      />
      {buyerLands.length > 0 ? (
        sectionTable(buyerLands)
      ) : (
        <Text type="secondary">— ไม่มีทรัพย์ของผู้เช่าซื้อ —</Text>
      )}

      {/* กลุ่มผู้ค้ำ */}
      <div style={{ marginTop: 20 }}>
        <SectionTitle
          color="purple"
          label="ทรัพย์ของผู้ค้ำประกัน"
          items={guarantorLands}
        />
        {guarantorLands.length > 0 ? (
          sectionTable(guarantorLands)
        ) : (
          <Text type="secondary">— ไม่มีทรัพย์ของผู้ค้ำประกัน —</Text>
        )}
      </div>

      <p>วันที่ยึด</p>
      <DatePicker showTime format="D MMM YYYY HH:mm" />
      <p>รายงานการยึด</p>
      <Dragger {...uploadProps} style={{ width: "100%", minHeight: 200 }}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined style={{ color: "blue", fontSize: 48 }} />
        </p>
        <p className="ant-upload-text" style={{ fontSize: 16 }}>
          คลิกหรือลากไฟล์มาวางที่นี่
        </p>
        <p className="ant-upload-hint" style={{ fontSize: 12 }}>
          รองรับการอัปโหลดหลายไฟล์พร้อมกัน
        </p>
      </Dragger>
    </Modal>
  );
};

export default SeizeAssetModal;
